import type { MetaFunction } from "react-router";
import { Link } from "react-router";
import { SITE_ORIGIN } from "../brand";
import {
  APP_STORE_URL,
  marketingLinks,
  MarketingShell,
  socialImageMeta,
} from "../components/marketing/layout";
import { ArrowRight, Check } from "../components/marketing/icons";

const PAGE_URL = `${SITE_ORIGIN}/pricing`;

export const links = marketingLinks;

const INCLUDED = [
  "Nutrition Facts panels (21 CFR 101.9)",
  "Supplement Facts panels (21 CFR 101.36)",
  "Live preview inside Shopify admin",
  "Automatic FDA rounding and % Daily Values",
  "USDA FoodData Central ingredient lookup",
  "300 DPI print-ready PNG export",
  "Labels saved as metaobjects on your own store",
  "Unlimited products, unlimited labels",
];

const FAQS = [
  {
    q: "What’s the catch?",
    a: "There isn’t one. No trial clock, no label cap, no watermark, no export fee. Install it and use it.",
  },
  {
    q: "Will it always be free?",
    a: "The current plan is free with every feature included. If a paid tier is ever introduced, anything you already use stays free for existing installs — we won’t put a working label behind a paywall after the fact.",
  },
  {
    q: "Do you take a cut of sales or charge per label?",
    a: "No. EasyNutriFact never touches your checkout, your orders, or your buyers. There is no per-label or per-export charge.",
  },
  {
    q: "Why free?",
    a: "Because the expensive part of labeling was never the software — it’s getting trustworthy numbers. We’d rather every small food brand have a correctly formatted panel than gate the format behind a subscription.",
  },
];

export const meta: MetaFunction = () => [
  { title: "Pricing — EasyNutriFact is free for Shopify merchants" },
  {
    name: "description",
    content:
      "Use EasyNutriFact to create Nutrition Facts and Supplement Facts panels with USDA lookup, rounding, Daily Values, and PNG export.",
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: PAGE_URL },
  { property: "og:url", content: PAGE_URL },
  { property: "og:type", content: "website" },
  { property: "og:title", content: "EasyNutriFact pricing — free" },
  {
    property: "og:description",
    content: "One plan. $0. Every feature included, unlimited labels.",
  },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  },
];

export default function Pricing() {
  return (
    <MarketingShell>
      <section className="nm-hero">
        <div className="nm-wrap">
          <div className="nm-head nm-head--center" style={{ marginInline: "auto" }}>
            <p className="nm-eyebrow nm-eyebrow--center nm-reveal" data-d="1">
              Pricing
            </p>
            <h1
              className="nm-reveal"
              data-d="2"
              style={{ fontSize: "var(--step-4)", marginTop: "1.2rem" }}
            >
              One plan. It&rsquo;s free.
            </h1>
            <p className="nm-hero__lede nm-reveal" data-d="3" style={{ marginInline: "auto" }}>
              No tiers to compare, no feature matrix to decode, no countdown on a
              trial. Every capability on the features page is in this plan.
            </p>
          </div>

          <div className="nm-plans nm-plans--single nm-reveal" data-d="3">
            <div className="nm-plan nm-plan--featured">
              <span className="nm-plan__flag">The only plan</span>
              <div className="nm-plan__name">Free</div>
              <p className="nm-plan__tagline">
                For every Shopify plan, every catalog size, every label.
              </p>
              <div className="nm-plan__price">
                <span className="nm-plan__amount">$0</span>
                <span className="nm-plan__cadence">/ forever</span>
              </div>
              <p className="nm-plan__trial">No card required · no trial clock</p>
              <div className="nm-plan__cta">
                <a className="nm-btn" href={APP_STORE_URL}>
                  Install free on Shopify
                  <ArrowRight />
                </a>
              </div>
              <ul className="nm-plan__list">
                <li className="is-head">Everything included:</li>
                {INCLUDED.map((f) => (
                  <li key={f}>
                    <Check />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="nm-section nm-band">
        <div className="nm-wrap nm-wrap--narrow">
          <div className="nm-head nm-head--center">
            <p className="nm-eyebrow nm-eyebrow--center">Fair questions</p>
            <h2>About the price being zero</h2>
          </div>
          <div className="nm-faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <div className="nm-faq__body">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="nm-section">
        <div className="nm-wrap">
          <div className="nm-cta nm-reveal" data-d="1">
            <h2>Label your first product tonight</h2>
            <p>
              The demo needs no account; the app needs no card. Start with
              whichever is closer.
            </p>
            <div className="nm-cta__actions">
              <a className="nm-btn" href={APP_STORE_URL}>
                Install free on Shopify
                <ArrowRight />
              </a>
              <Link className="nm-btn nm-btn--ghost" to="/demo">
                Try the live demo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
